import type { OrganizationDTOV2 } from '@workers-benefit-fund/api'

import { cleanStringToBeUrlReadable } from 'src/default/helpers/clean-string'

import { getLastExplicitLoginAt } from './organizationSwitchSession.utils'

export const LAST_VALID_ORGANIZATION_LOCATION_STORAGE_KEY =
  'w360-organization-last-valid-location'

type OrganizationContext = Pick<OrganizationDTOV2, 'id' | 'name'>

interface SavedOrganizationLocation {
  organizationId: string
  path: string
  timestamp: number
}

const getSafeOrganizationPath = (
  path: unknown,
  organization: OrganizationContext,
): string | null => {
  if (
    !organization.id ||
    !organization.name ||
    typeof path !== 'string' ||
    !path.startsWith('/') ||
    path.startsWith('//')
  ) {
    return null
  }

  const url = new URL(path, window.location.origin)
  const organizationPrefix = `/${cleanStringToBeUrlReadable(organization.name)}`
  if (
    url.origin !== window.location.origin ||
    (url.pathname !== organizationPrefix &&
      !url.pathname.startsWith(`${organizationPrefix}/`))
  ) {
    return null
  }

  return `${url.pathname}${url.search}${url.hash}`
}

export const rememberLastValidOrganizationLocation = (
  organization: OrganizationContext,
  path: string,
) => {
  try {
    const safePath = getSafeOrganizationPath(path, organization)
    if (!safePath) return

    const location: SavedOrganizationLocation = {
      organizationId: organization.id,
      path: safePath,
      timestamp: Date.now(),
    }
    sessionStorage.setItem(
      LAST_VALID_ORGANIZATION_LOCATION_STORAGE_KEY,
      JSON.stringify(location),
    )
  } catch {
    // Navigation can still fall back to the landing page if storage is unavailable.
  }
}

export const getLastValidOrganizationLocation = (
  organization: OrganizationContext,
): string | null => {
  try {
    const serialized = sessionStorage.getItem(
      LAST_VALID_ORGANIZATION_LOCATION_STORAGE_KEY,
    )
    if (!serialized) return null

    const location = JSON.parse(serialized) as SavedOrganizationLocation
    if (
      location.organizationId !== organization.id ||
      !Number.isFinite(location.timestamp) ||
      location.timestamp < getLastExplicitLoginAt()
    ) {
      return null
    }

    return getSafeOrganizationPath(location.path, organization)
  } catch {
    return null
  }
}
